import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { AutomationBlocker } from '@rossum/api-client/internal';
import { Message } from '@rossum/api-client/shared';
import {
  SchemaFieldIcon,
  SchemaFieldIconType,
} from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import {
  IconAlertOctagon,
  IconAlertTriangle,
  IconBolt,
} from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import HTMLMessage from '../../../../components/DatapointMessage/HTMLMessage';

type AggregateTooltipProps<I> = {
  items?: Array<I>;
  Icon: JSX.Element | null;
};

const NUMBER_OF_VISIBLE_MESSAGES = 10;

const AggregateTooltip = <
  I extends { content: string; id?: string | number | null },
>({
  items: incomingItems,
  Icon,
}: AggregateTooltipProps<I>) => {
  const intl = useIntl();
  const items = R.uniqueBy(incomingItems ?? [], item => item.content);
  return items.length && Icon ? (
    <Tooltip
      title={
        <>
          {items.slice(0, NUMBER_OF_VISIBLE_MESSAGES).map(({ id, content }) => {
            return (
              <Typography variant="body2" key={id + content} component="div">
                <HTMLMessage content={content} />
              </Typography>
            );
          })}
          {items.slice(NUMBER_OF_VISIBLE_MESSAGES).length ? (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage(
                {
                  id: 'features.queueSettings.fields.formulaPreview.table.moreLines',
                },
                { count: items.slice(NUMBER_OF_VISIBLE_MESSAGES).length }
              )}
            </Typography>
          ) : null}
        </>
      }
    >
      {Icon}
    </Tooltip>
  ) : null;
};

const messagesIcons = {
  error: IconAlertOctagon,
  warning: IconAlertTriangle,
  info: IconAlertOctagon,
};

type CellIconsProps = {
  type: SchemaFieldIconType | null;
  messages?: Array<Message>;
  automationBlockers?: Array<AutomationBlocker>;
};

export const CellIcons = ({
  type,
  messages,
  automationBlockers,
}: CellIconsProps) => {
  const firstMessageType = messages?.[0]?.type ?? 'error';
  const FirstMessageIcon = messagesIcons[firstMessageType];

  return (
    <Stack direction="row" spacing={1}>
      <AggregateTooltip
        items={messages}
        Icon={
          <SvgIcon fontSize="medium" color={firstMessageType}>
            <FirstMessageIcon />
          </SvgIcon>
        }
      />
      <AggregateTooltip
        items={automationBlockers}
        Icon={
          <SvgIcon fontSize="medium" color="error">
            <IconBolt />
          </SvgIcon>
        }
      />
      {type ? (
        <SchemaFieldIcon
          type={type}
          sx={{ color: 'text.secondary' }}
          fontSize="medium"
        />
      ) : null}
    </Stack>
  );
};
